import classNames from 'classnames';
// import isAfter from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { parseISO } from 'date-fns/parseISO';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { getImageAtSize } from '../../../lib/utils';

import { useThemeClassName } from '../../../contexts/ThemeContext';
import Button from '../../buttons/Button';

import styles from '../../../../styles/items/games/rhoneacademy.module.scss';

interface RhoneacademyGameItemProps {
    game: ExamGame;
    className?: string | null;
    cardClassName?: string | null;
}

function RhoneacademyGameItem({
    game,
    className = null,
    cardClassName = null,
}: RhoneacademyGameItemProps) {
    const themeClassName = useThemeClassName(styles);
    const {
        type,
        url,
        title = null,
        subtitle = null,
        description = null,
        image = null,
        startAt = null,
        endAt = null,
    } = game || {};

    const startAtDate = useMemo(() => (startAt !== null ? parseISO(startAt) : null), [startAt]);
    const endAtDate = useMemo(() => (endAt !== null ? parseISO(endAt) : null), [endAt]);

    const isStarted = useMemo(
        () => startAtDate === null || isBefore(startAtDate, new Date()),
        [startAtDate],
    );
    const isEnded = useMemo(
        () => endAtDate !== null && isBefore(endAtDate, new Date()),
        [endAtDate],
    );

    const inactive = useMemo(() => !isStarted || isEnded, [startAtDate, endAtDate]);

    const {
        url: imageUrl = null,
        width: imageWidth = null,
        height: imageHeight = null,
    } = getImageAtSize(image, 'medium') || {};

    return (
        <div
            className={classNames([
                styles.container,
                styles[type],
                {
                    [styles.inactive]: inactive,
                },
                themeClassName,
                className,
            ])}
        >
            <div className={styles.inner}>
                <Button
                    href={isStarted && !isEnded ? url : null}
                    disabled={!isStarted || isEnded}
                    className={classNames([
                        styles.card,
                        { [cardClassName]: cardClassName !== null },
                    ])}
                >
                    {imageUrl !== null ? (
                        <div className={styles.imageContainer}>
                            <img src={imageUrl} alt={title} className={styles.image} />
                        </div>
                    ) : null}

                    <div className={styles.title}>{title}</div>
                    <div className={styles.subtitle}>{subtitle}</div>

                    <div className={styles.date}>
                        {isEnded ? (
                            <FormattedMessage
                                defaultMessage="Ended on {date}"
                                values={{
                                    date: (
                                        <FormattedDate
                                            value={endAtDate}
                                            day="numeric"
                                            month="short"
                                        />
                                    ),
                                }}
                                description="Button label"
                            />
                        ) : null}
                        {!isStarted ? (
                            <FormattedMessage
                                defaultMessage="Starting on {date}"
                                values={{
                                    date: (
                                        <FormattedDate
                                            value={startAtDate}
                                            day="numeric"
                                            month="short"
                                        />
                                    ),
                                }}
                                description="Button label"
                            />
                        ) : null}
                        {isStarted && !isEnded ? (
                            <FormattedMessage defaultMessage="Play" description="Button label" />
                        ) : null}
                    </div>
                    <div className={styles.description}>{description}</div>
                </Button>
            </div>
        </div>
    );
}

export default RhoneacademyGameItem;
